import { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import Upgrade from './Upgrade'
import Settings from './Settings'
import Pod from './Pod'
import Coach from './Coach'
import PlantVisual from '../components/PlantVisual'
import MilestoneModal from '../components/MilestoneModal'
import { localDateString } from '../utils/date'

const URGE_ACTIVITIES = [
  { emoji: '🏃', title: 'Go for a walk', desc: 'Step outside for 5 minutes. Fresh air breaks the pattern.' },
  { emoji: '💧', title: 'Drink cold water', desc: 'Drink a full glass of cold water right now. Slowly.' },
  { emoji: '🫁', title: 'Box breathing', desc: 'Breathe in 4 counts, hold 4, out 4, hold 4. Repeat 4 times.' },
  { emoji: '📱', title: 'Text someone', desc: 'Text a friend or family member something positive right now.' },
  { emoji: '💪', title: 'Do 10 pushups', desc: 'Drop and do 10 pushups. Move your body, break the cycle.' },
  { emoji: '🎵', title: 'Play a song', desc: 'Put on your favorite upbeat song and just listen.' },
  { emoji: '🧊', title: 'Cold splash', desc: 'Splash cold water on your face. Resets your nervous system instantly.' },
  { emoji: '✍️', title: 'Write it down', desc: 'Grab your phone and write one sentence about what you\'re feeling.' },
  { emoji: '🧹', title: 'Clean something', desc: 'Tidy one small area. Redirects energy and gives instant satisfaction.' },
  { emoji: '🌳', title: 'Step outside', desc: 'Go outside and look at the sky for 60 seconds. Just breathe.' },
]

function UrgeModal({ onClose, onSlip }) {
  const [timer, setTimer] = useState(120)
  const [beaten, setBeaten] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [activity] = useState(
    URGE_ACTIVITIES[Math.floor(Math.random() * URGE_ACTIVITIES.length)]
  )

  useEffect(() => {
    const interval = setInterval(() => {
      setTimer(t => {
        if (t <= 1) {
          clearInterval(interval)
          return 0
        }
        return t - 1
      })
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  const mins = Math.floor(timer / 60)
  const secs = String(timer % 60).padStart(2, '0')

  return (
    <div style={modalStyles.overlay}>
      <div style={modalStyles.card}>
        {beaten ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>🏆</div>
            <h2 style={modalStyles.title}>You beat it.</h2>
            <p style={modalStyles.sub}>Every win matters. Your streak is intact.</p>
            <button style={modalStyles.mainBtn} onClick={onClose}>
              Back to dashboard
            </button>
          </div>
        ) : confirming ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>💔</div>
            <h2 style={modalStyles.title}>Log a slip?</h2>
            <p style={{ ...modalStyles.sub, marginBottom: 28 }}>
              Slips are part of recovery — they don't erase your progress. But this will reset your streak to zero.
            </p>
            <button style={modalStyles.mainBtn} onClick={onClose}>
              Cancel — keep going
            </button>
            <button style={modalStyles.confirmSlipBtn} onClick={() => { onSlip(); onClose() }}>
              Yes, log the slip
            </button>
          </div>
        ) : (
          <>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div style={{ fontSize: 48, marginBottom: 8 }}>🆘</div>
              <h2 style={modalStyles.title}>Urge logged. You've got this.</h2>
              <p style={modalStyles.sub}>Try this right now:</p>
            </div>

            <div style={modalStyles.activityCard}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>{activity.emoji}</div>
              <p style={modalStyles.activityTitle}>{activity.title}</p>
              <p style={modalStyles.activityDesc}>{activity.desc}</p>
            </div>

            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <p style={modalStyles.timerText}>{mins}:{secs}</p>
              <p style={modalStyles.timerSub}>Most urges pass in 2 minutes</p>
            </div>

            <button style={modalStyles.mainBtn} onClick={() => setBeaten(true)}>
              I stayed strong ✓
            </button>

            <button style={modalStyles.slipBtn} onClick={() => setConfirming(true)}>
              I slipped — that's okay
            </button>
          </>
        )}
      </div>
    </div>
  )
}

const modalStyles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.9)',
    backdropFilter: 'blur(12px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    zIndex: 200,
  },
  card: {
    width: '100%',
    maxWidth: 360,
    background: 'linear-gradient(145deg, #1a1040, #0d0d1a)',
    border: '1px solid rgba(167,139,250,0.3)',
    borderRadius: 24,
    padding: 32,
  },
  title: {
    color: '#fff',
    fontFamily: 'serif',
    fontSize: 22,
    margin: '0 0 8px',
  },
  sub: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: 14,
    margin: 0,
  },
  activityCard: {
    background: 'rgba(167,139,250,0.08)',
    border: '1px solid rgba(167,139,250,0.2)',
    borderRadius: 16,
    padding: 20,
    textAlign: 'center',
    marginBottom: 20,
  },
  activityTitle: {
    color: '#c4b5fd',
    fontSize: 18,
    fontWeight: 700,
    margin: '0 0 8px',
  },
  activityDesc: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 14,
    margin: 0,
    lineHeight: 1.6,
  },
  timerText: {
    color: '#a78bfa',
    fontSize: 48,
    fontFamily: 'serif',
    margin: 0,
  },
  timerSub: {
    color: 'rgba(255,255,255,0.35)',
    fontSize: 12,
    margin: '4px 0 0',
  },
  mainBtn: {
    width: '100%',
    padding: 15,
    background: 'linear-gradient(135deg, #7c3aed, #2563eb)',
    border: 'none',
    borderRadius: 12,
    color: '#fff',
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    marginBottom: 10,
  },
  slipBtn: {
    width: '100%',
    padding: 12,
    background: 'none',
    border: 'none',
    color: 'rgba(255,255,255,0.3)',
    fontSize: 13,
    cursor: 'pointer',
  },
  confirmSlipBtn: {
    width: '100%',
    padding: 12,
    background: 'none',
    border: '1px solid rgba(239,68,68,0.25)',
    borderRadius: 12,
    color: 'rgba(248,113,113,0.6)',
    fontSize: 13,
    cursor: 'pointer',
    marginTop: 8,
  },
}

function getBehaviorStat(behavior, streak) {
  const savingsPerDay = {
    alcohol: 15,
    drugs: 30,
    smoking: 10,
    gambling: 20,
    porn: 3,
  }
  const daysOnly = ['nails', 'social_media', 'skin_picking']
  if (daysOnly.includes(behavior)) return `${streak} days`
  const saved = (savingsPerDay[behavior] || 0) * streak
  return `$${saved}`
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)))
}

export default function Dashboard({ session }) {
  const [streak, setStreak] = useState(0)
  const [behavior, setBehavior] = useState(null)
  const [loading, setLoading] = useState(true)
  const [urgeLogged, setUrgeLogged] = useState(false)
  const [checkedIn, setCheckedIn] = useState(false)
  const [showUpgrade, setShowUpgrade] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [showUrgeModal, setShowUrgeModal] = useState(false)
  const [activeTab, setActiveTab] = useState('home')
  const [isPro, setIsPro] = useState(false)
  // 'unsupported' | 'prompt' | 'denied' | 'subscribed'
  const [notifStatus, setNotifStatus] = useState('unsupported')
  const [plantStreak, setPlantStreak] = useState(0)
  const [wilting, setWilting] = useState(false)
  const [milestone, setMilestone] = useState(null)

  useEffect(() => {
    async function loadData() {
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .single()

      if (profile) {
        setBehavior(profile.behavior)
        setIsPro(profile.is_pro || false)
      }

      const { data: streakData } = await supabase
        .from('streaks')
        .select('*')
        .eq('user_id', session.user.id)
        .eq('is_active', true)
        .single()

      if (streakData) {
        setStreak(streakData.days_count)
        setPlantStreak(streakData.days_count)
      }

      const today = localDateString()
      const { data: checkinData } = await supabase
        .from('checkins')
        .select('*')
        .eq('user_id', session.user.id)
        .eq('date', today)
        .single()

      if (checkinData) setCheckedIn(true)

      if ('Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window) {
        if (Notification.permission === 'denied') {
          setNotifStatus('denied')
        } else {
          const { data: sub } = await supabase
            .from('push_subscriptions')
            .select('id')
            .eq('user_id', session.user.id)
            .single()
          setNotifStatus(sub ? 'subscribed' : 'prompt')
        }
      }

      setLoading(false)
    }

    loadData()
  }, [session])

  async function logUrge() {
    await supabase.from('urge_logs').insert({
      user_id: session.user.id,
      behavior: behavior,
      resisted: true,
    })
    setUrgeLogged(true)
    setTimeout(() => setUrgeLogged(false), 3000)
  }

  async function subscribeToNotifications() {
    try {
      const permission = await Notification.requestPermission()
      if (permission !== 'granted') {
        setNotifStatus('denied')
        return
      }
      const reg = await navigator.serviceWorker.ready
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(process.env.REACT_APP_VAPID_PUBLIC_KEY),
      })
      await supabase.from('push_subscriptions').upsert({
        user_id: session.user.id,
        subscription: sub.toJSON(),
      })
      setNotifStatus('subscribed')
    } catch (err) {
      console.error('push subscription failed:', err)
    }
  }

  async function logCheckin(stayedClean) {
    if (checkedIn) return

    const today = localDateString()

    await supabase.from('checkins').insert({
      user_id: session.user.id,
      date: today,
      stayed_clean: stayedClean,
    })

    if (stayedClean) {
      const { data: streakData } = await supabase
        .from('streaks')
        .select('*')
        .eq('user_id', session.user.id)
        .eq('is_active', true)
        .single()

      if (streakData) {
        await supabase
          .from('streaks')
          .update({
            days_count: streakData.days_count + 1,
            last_checkin: today,
          })
          .eq('id', streakData.id)
        const newCount = streakData.days_count + 1
        setStreak(newCount)
        setPlantStreak(newCount)
        if ([7, 30, 90].includes(newCount)) setMilestone(newCount)
      }
    } else {
      await supabase
        .from('streaks')
        .update({ is_active: false })
        .eq('user_id', session.user.id)
        .eq('is_active', true)

      await supabase.from('streaks').insert({
        user_id: session.user.id,
        behavior: behavior,
        start_date: today,
        days_count: 0,
        is_active: true,
      })
      setStreak(0)
      // Wilt the plant, then reset to seed after the animation completes
      setWilting(true)
      setTimeout(() => {
        setPlantStreak(0)
        setWilting(false)
      }, 2200)
    }

    setCheckedIn(true)
  }

  if (loading) return <div style={styles.loading}>⚓</div>

  return (
    <div style={styles.wrapper}>

      <div style={styles.content}>

        {activeTab === 'home' && (
          <div style={styles.inner}>
            <div style={styles.header}>
              <div>
                <p style={styles.greeting}>Good day 👋</p>
                <p style={styles.behaviorTag}>
                  {behavior ? `Working on: ${behavior}` : 'No behavior set'}
                </p>
              </div>
              <button
                style={styles.settingsBtn}
                onClick={() => setShowSettings(true)}
              >
                ⚙️
              </button>
            </div>

            <div style={styles.streakCard}>
              <PlantVisual streak={plantStreak} wilting={wilting} />
              <p style={styles.streakNumber}>{streak}</p>
              <p style={styles.streakLabel}>Day Streak</p>
              {streak >= 7 && (
                <p style={styles.milestone}>🔥 {streak} days — keep going!</p>
              )}
            </div>

            {urgeLogged ? (
              <div style={styles.urgeConfirm}>
                <p style={styles.urgeConfirmText}>
                  💪 Urge logged. You've got this.
                </p>
              </div>
            ) : (
              <button style={styles.urgeBtn} onClick={() => {
                logUrge()
                setShowUrgeModal(true)
              }}>
                🆘 I'm Having an Urge
              </button>
            )}

            {checkedIn ? (
              <div style={styles.checkedInCard}>
                <p style={styles.checkedInText}>✅ You've already checked in today</p>
                <p style={styles.checkedInSub}>Come back tomorrow to keep your streak alive.</p>
              </div>
            ) : (
              <div style={styles.checkinCard}>
                <p style={styles.checkinTitle}>Did you stay clean today?</p>
                <div style={styles.checkinRow}>
                  <button style={styles.yesBtn} onClick={() => logCheckin(true)}>
                    Yes ✓
                  </button>
                  <button style={styles.noBtn} onClick={() => logCheckin(false)}>
                    I slipped
                  </button>
                </div>
              </div>
            )}

            <div style={styles.statsRow}>
              <div style={styles.statCard}>
                <p style={styles.statNumber}>{streak}</p>
                <p style={styles.statLabel}>Current Streak</p>
              </div>
              <div style={styles.statCard}>
                <p style={styles.statNumber}>
                  {getBehaviorStat(behavior, streak)}
                </p>
                <p style={styles.statLabel}>
                  {behavior === 'nails' ? 'Days Strong' : 'Est. Saved'}
                </p>
              </div>
            </div>

            {notifStatus === 'prompt' && (
              <button style={styles.notifBtn} onClick={subscribeToNotifications}>
                🔔 Enable daily reminders
              </button>
            )}

            {!isPro && (
              <button
                style={styles.proBtn}
                onClick={() => setShowUpgrade(true)}
              >
                ⚡ Unlock Anchor Pro — $9.99/mo
              </button>
            )}
          </div>
        )}

        {activeTab === 'pod' && isPro && (
          <Pod session={session} behavior={behavior} />
        )}

        {activeTab === 'pod' && !isPro && (
          <div style={styles.lockedScreen}>
            <div style={{ fontSize: 56, marginBottom: 16 }}>🔒</div>
            <h2 style={styles.lockedTitle}>Pods are a Pro Feature</h2>
            <p style={styles.lockedSub}>
              Join an accountability pod with real people fighting the same battle. Upgrade to Anchor Pro to unlock.
            </p>
            <button style={styles.unlockBtn} onClick={() => setShowUpgrade(true)}>
              Unlock Pro — $9.99/mo
            </button>
          </div>
        )}

        {activeTab === 'coach' && isPro && (
          <Coach session={session} behavior={behavior} streak={streak} />
        )}

        {activeTab === 'coach' && !isPro && (
          <div style={styles.lockedScreen}>
            <div style={{ fontSize: 56, marginBottom: 16 }}>🤖</div>
            <h2 style={styles.lockedTitle}>AI Coach is a Pro Feature</h2>
            <p style={styles.lockedSub}>
              Get 24/7 support from an AI recovery coach that knows your journey. Upgrade to unlock.
            </p>
            <button style={styles.unlockBtn} onClick={() => setShowUpgrade(true)}>
              Unlock Pro — $9.99/mo
            </button>
          </div>
        )}

      </div>

      <div style={styles.bottomNav}>
        {[
          { id: 'home', icon: '🏠', label: 'Home' },
          { id: 'pod', icon: '👥', label: 'Pod' },
          { id: 'coach', icon: '🤖', label: 'Coach' },
        ].map(tab => (
          <button
            key={tab.id}
            style={styles.navBtn}
            onClick={() => setActiveTab(tab.id)}
          >
            <span style={{ fontSize: 22 }}>{tab.icon}</span>
            <span style={{
              fontSize: 10,
              color: activeTab === tab.id ? '#a78bfa' : 'rgba(255,255,255,0.3)',
              fontWeight: activeTab === tab.id ? 700 : 400,
            }}>
              {tab.label}
            </span>
          </button>
        ))}
      </div>

      {milestone && (
        <MilestoneModal days={milestone} onClose={() => setMilestone(null)} />
      )}

      {showUrgeModal && (
        <UrgeModal
          onClose={() => setShowUrgeModal(false)}
          onSlip={() => { logCheckin(false); setShowUrgeModal(false) }}
        />
      )}

      {showUpgrade && (
        <Upgrade session={session} onClose={() => setShowUpgrade(false)} />
      )}

      {showSettings && (
        <Settings
          session={session}
          currentBehavior={behavior}
          onUpdate={(newBehavior) => {
            setBehavior(newBehavior)
            setStreak(0)
            setCheckedIn(false)
          }}
          onClose={() => setShowSettings(false)}
        />
      )}

    </div>
  )
}

const styles = {
  loading: {
    minHeight: '100vh',
    background: '#080812',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 48,
  },
  wrapper: {
    minHeight: '100vh',
    background: '#080812',
    display: 'flex',
    flexDirection: 'column',
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    paddingBottom: 80,
  },
  inner: {
    width: '100%',
    maxWidth: 400,
    margin: '0 auto',
    padding: '40px 20px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 28,
  },
  greeting: {
    color: '#fff',
    fontSize: 22,
    fontFamily: 'serif',
    margin: 0,
  },
  behaviorTag: {
    color: '#a78bfa',
    fontSize: 13,
    margin: '4px 0 0',
    textTransform: 'capitalize',
  },
  settingsBtn: {
    background: 'none',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 8,
    fontSize: 18,
    padding: '6px 10px',
    cursor: 'pointer',
  },
  streakCard: {
    background: 'linear-gradient(135deg, #1a0a3a, #0a1a3a)',
    border: '1px solid rgba(167,139,250,0.2)',
    borderRadius: 24,
    padding: '28px 40px 32px',
    textAlign: 'center',
    marginBottom: 16,
  },
  streakNumber: {
    color: '#fff',
    fontSize: 80,
    fontFamily: 'serif',
    margin: 0,
    lineHeight: 1,
  },
  streakLabel: {
    color: 'rgba(255,255,255,0.45)',
    fontSize: 14,
    margin: '8px 0 0',
  },
  milestone: {
    color: '#a78bfa',
    fontSize: 13,
    margin: '12px 0 0',
  },
  urgeBtn: {
    width: '100%',
    padding: 20,
    background: 'linear-gradient(135deg, #991b1b, #7f1d1d)',
    border: '1px solid rgba(239,68,68,0.3)',
    borderRadius: 20,
    color: '#fca5a5',
    fontSize: 17,
    fontWeight: 800,
    cursor: 'pointer',
    marginBottom: 12,
  },
  urgeConfirm: {
    background: 'rgba(167,139,250,0.1)',
    border: '1px solid rgba(167,139,250,0.2)',
    borderRadius: 20,
    padding: 20,
    textAlign: 'center',
    marginBottom: 12,
  },
  urgeConfirmText: {
    color: '#a78bfa',
    fontSize: 15,
    fontWeight: 600,
    margin: 0,
  },
  checkinCard: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 20,
    padding: 20,
    marginBottom: 16,
  },
  checkinTitle: {
    color: '#fff',
    fontSize: 15,
    fontWeight: 600,
    margin: '0 0 12px',
  },
  checkinRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 10,
  },
  yesBtn: {
    padding: 14,
    borderRadius: 12,
    background: 'rgba(74,222,128,0.1)',
    border: '1px solid rgba(74,222,128,0.3)',
    color: '#4ade80',
    fontWeight: 700,
    fontSize: 14,
    cursor: 'pointer',
  },
  noBtn: {
    padding: 14,
    borderRadius: 12,
    background: 'rgba(239,68,68,0.1)',
    border: '1px solid rgba(239,68,68,0.3)',
    color: '#f87171',
    fontWeight: 700,
    fontSize: 14,
    cursor: 'pointer',
  },
  checkedInCard: {
    background: 'rgba(74,222,128,0.08)',
    border: '1px solid rgba(74,222,128,0.2)',
    borderRadius: 20,
    padding: 20,
    textAlign: 'center',
    marginBottom: 16,
  },
  checkedInText: {
    color: '#4ade80',
    fontSize: 15,
    fontWeight: 600,
    margin: '0 0 4px',
  },
  checkedInSub: {
    color: 'rgba(74,222,128,0.55)',
    fontSize: 12,
    margin: 0,
  },
  statsRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
    marginBottom: 12,
  },
  statCard: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: 16,
    padding: 18,
    textAlign: 'center',
  },
  statNumber: {
    color: '#fff',
    fontSize: 28,
    fontFamily: 'serif',
    margin: 0,
  },
  statLabel: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: 11,
    margin: '4px 0 0',
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  notifBtn: {
    width: '100%',
    padding: 14,
    background: 'rgba(167,139,250,0.08)',
    border: '1px solid rgba(167,139,250,0.2)',
    borderRadius: 14,
    color: '#a78bfa',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 12,
  },
  proBtn: {
    width: '100%',
    padding: 16,
    background: 'linear-gradient(135deg, #4c1d95, #1e3a8a)',
    border: '1px solid rgba(167,139,250,0.4)',
    borderRadius: 14,
    color: '#fff',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    marginTop: 12,
  },
  lockedScreen: {
    padding: '60px 20px',
    textAlign: 'center',
    maxWidth: 400,
    margin: '0 auto',
  },
  lockedTitle: {
    color: '#fff',
    fontFamily: 'serif',
    fontSize: 24,
    marginBottom: 12,
  },
  lockedSub: {
    color: 'rgba(255,255,255,0.45)',
    fontSize: 14,
    marginBottom: 32,
    lineHeight: 1.6,
  },
  unlockBtn: {
    width: '100%',
    padding: 16,
    background: 'linear-gradient(135deg, #7c3aed, #2563eb)',
    border: 'none',
    borderRadius: 14,
    color: '#fff',
    fontSize: 16,
    fontWeight: 700,
    cursor: 'pointer',
  },
  bottomNav: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    background: 'rgba(8,8,18,0.97)',
    borderTop: '1px solid rgba(255,255,255,0.07)',
    display: 'flex',
    justifyContent: 'space-around',
    padding: '12px 0 20px',
    zIndex: 50,
  },
  navBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    padding: '0 24px',
  },
}